import './overlays.css';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import {
    EffectComposer,
    EffectPass,
    RenderPass,
    BloomEffect,
    VignetteEffect,
    SMAAEffect,
    SMAAPreset,
    ToneMappingEffect,
    ToneMappingMode,
    SSAOEffect,
    NormalPass,
    GodRaysEffect,
    DepthOfFieldEffect,
    KernelSize
} from 'postprocessing';
import * as dat from 'dat.gui';
import { createSky, type SkyRig } from './sky';
import { GoogleTiles, TILES_KEY } from './googleTiles';
import { device } from './deviceProfile';
import { DayNightController } from './dayNight';
import { sliderForNow } from './solar';
import {
    m,
    MODEL_HEADING_DEG,
    sceneScale,
    setSceneScaleFromModelWidth,
    TERRAIN_CLEARANCE_M,
} from './site';
import { createWalkMode, type WalkRig } from './walkMode';
import { createApartmentBlocks, type ApartmentBlocksRig } from './apartmentBlocks';
import { createDesertDecor, type DesertDecor } from './decor';
import { createPedestrians, type PedestrianRig } from './pedestrians';
import { loadLayout, type PlacementRuntime } from '../placement/runtime';
import type { PlacementContext } from '../placement/types';

// Type definitions
interface DevSettings {
    exposure: number;
    toneMapping: string;
    ambientIntensity: number;
    ambientColor: string;
    directIntensity: number;
    directColor: string;
    punctualLights: boolean;
    envIntensity: number;
    showEnvBackground: boolean;
    bloomEnabled: boolean;
    bloomIntensity: number;
    bloomLuminanceThreshold: number;
    bloomLuminanceSmoothing: number;
    bloomRadius: number;
    vignetteEnabled: boolean;
    vignetteOffset: number;
    vignetteDarkness: number;
    ssaoEnabled: boolean;
    ssaoIntensity: number;
    ssaoRadius: number;
    ssaoSamples: number;
    ssaoBias: number;
    modelScale: number;
    topViewZoom: number;
    topViewMinZoom: number;
    topViewMaxZoom: number;
    fitToView: () => void;
    logCamera: () => void;
}

interface GUIFolders {
    cameras: dat.GUI;
    pointLights: dat.GUI;
    spotLights: dat.GUI;
}

// Hotspot interface
interface Hotspot {
    id: string;
    position: THREE.Vector3;
    title: string;
    description: string;
    mesh: THREE.Mesh;
    label: HTMLElement;
    // Apartment linking
    apartmentIndex: number | null;  // null = no link, 1-3 = apartment index
    mediaUrl: string;               // optional image/video URL
    mediaType: 'image' | 'video' | null;
    color: string;                  // hex color for hotspot marker
}

// Extend Window interface
declare global {
    interface Window {
        guiFolders: GUIFolders;
        hotspots: Hotspot[];
        hotspotEditMode: boolean;
    }
}

// Console error tracking
window.addEventListener('error', (e: ErrorEvent) => {
    console.error('Global error:', e.message, e.filename, e.lineno, e.colno);
});

// Three.js variables
let scene: THREE.Scene;
let camera: THREE.PerspectiveCamera;
let renderer: THREE.WebGLRenderer;
let controls: OrbitControls;
let model: THREE.Group;
let ambientLight: THREE.AmbientLight;
let glbLights: THREE.Light[] = [];
let gui: dat.GUI;
let tennisBalls: THREE.Object3D[] = [];
let envMap: THREE.Texture;
let composer: EffectComposer;
let bloomEffect: BloomEffect;
let vignetteEffect: VignetteEffect;
let smaaEffect: SMAAEffect;
let toneMappingEffect: ToneMappingEffect;
let ssaoEffect: SSAOEffect | null = null;
let godRaysEffect: GodRaysEffect | null = null;
let dofEffect: DepthOfFieldEffect | null = null;

// Cameras and lights from GLB
let glbCameras: THREE.Camera[] = [];
let glbPointLights: THREE.PointLight[] = [];
let glbSpotLights: THREE.SpotLight[] = [];

// Track if camera controls are locked (for Top View)
let cameraLocked = false;

// Environment: procedural sky, real solar position, Google 3D Tiles context
let skyRig: SkyRig;
let keyLight: THREE.DirectionalLight;
let dayNight: DayNightController;
let googleTiles: GoogleTiles | null = null;
let liveTime = true;
let rafHandle: number | null = null;
let disposed = false;
let keyHandler: ((e: KeyboardEvent) => void) | null = null;
let onProgress: ((percent: number) => void) | null = null;
let onLoaded: (() => void) | null = null;
let liveTimeTimer = 0;
let lastEnvTime = 0;

// Where the residence assets are served from.
//
// Local by default. The CDN copies at https://media.augmento.com/3d/ are the
// same files, but that bucket sends no Access-Control-Allow-Origin header, so
// the browser blocks them cross-origin (curl succeeds, which is misleading:
// curl does not enforce CORS). Enable CORS on the bucket and this one constant
// can point back at it.
const APARTMENT_BASE = 'apartments/';

// Environment fill for the residence takeover. Held separate from the
// exterior's, which the day/night controller drives up to 2.6 for a sunlit
// masterplan: that is far too much light for a room.
const POPUP_ENV_INTENSITY = 0.9;

// Glowing residence volumes, replacing the pulsing dot markers
let apartmentBlocks: ApartmentBlocksRig | null = null;

// Near-field set dressing: palms, sand, suggested town
let decor: DesertDecor | null = null;

// People on the masterplan's paving
let pedestrians: PedestrianRig | null = null;
let placementRuntime: PlacementRuntime | null = null;
let placementContext: PlacementContext | null = null;

// First-person walk-through
let walk: WalkRig | null = null;
let onWalkExit: (() => void) | null = null;

// Hotspot system
let hotspots: Hotspot[] = [];
let hotspotEditMode = false;
let hotspotContainer: HTMLElement;
let selectedHotspot: Hotspot | null = null;
let defaultHotspotColor = '#00BFFF'; // Electric blue

// Apartment model system
interface ApartmentModel {
    name: string;
    file: string;
    model: THREE.Group | null;
    loaded: boolean;
}

const apartmentModels: ApartmentModel[] = [
    { name: 'None (Main View)', file: '', model: null, loaded: true },
    { name: '2 Bedroom', file: APARTMENT_BASE + 'Diriyah_2bedroom(c).glb', model: null, loaded: false },
    { name: '3 Bedroom', file: APARTMENT_BASE + 'Diriyah_3bedroom(c).glb', model: null, loaded: false },
    { name: 'Penthouse', file: APARTMENT_BASE + 'Diriyah_PH(c).glb', model: null, loaded: false }
];

let currentApartment: ApartmentModel | null = null;
let isTransitioning = false;

// Apartment Popup System
let apartmentPopupActive = false;
let apartmentPopupRenderer: THREE.WebGLRenderer | null = null;
let apartmentPopupScene: THREE.Scene | null = null;
let apartmentPopupCamera: THREE.PerspectiveCamera | null = null;
let apartmentPopupControls: OrbitControls | null = null;
let apartmentPopupComposer: EffectComposer | null = null;
let currentPopupHotspot: Hotspot | null = null;
let mainRenderPaused = false;

// Initialize hotspot globals
window.hotspots = hotspots;
window.hotspotEditMode = hotspotEditMode;

// Dev controls settings
const devSettings: DevSettings = {
    exposure: -2,
    toneMapping: 'Linear',
    ambientIntensity: 0.3,
    ambientColor: '#ffffff',
    directIntensity: 2.5,
    directColor: '#ffffff',
    punctualLights: true,
    envIntensity: 2.0,
    showEnvBackground: false,
    bloomEnabled: true,
    bloomIntensity: 0.17,
    bloomLuminanceThreshold: 1,
    bloomLuminanceSmoothing: 0.025,
    bloomRadius: 0.44,
    vignetteEnabled: true,
    vignetteOffset: 0.3,
    vignetteDarkness: 0.5,
    ssaoEnabled: true,
    ssaoIntensity: 1.5,
    ssaoRadius: 0.08,
    ssaoSamples: 8, // Reduced from 16 for performance
    ssaoBias: 0.025,
    modelScale: 2.5,
    topViewZoom: 1.5,
    topViewMinZoom: 20,
    topViewMaxZoom: 150,
    fitToView: () => fitCameraToModel(),
    logCamera: () => {
        console.log('Camera Position:', camera.position);
        console.log('Controls Target:', controls.target);
    }
};

// Initialize Three.js
function initThreeJS(): void {
    const container = document.getElementById('threejs-container');

    if (!container) {
        console.error('Container not found');
        return;
    }

    // Create scene
    scene = new THREE.Scene();

    // Procedural atmosphere replaces the baked sunset JPG: it is driven by the
    // real solar position for Diriyah, so the sky, the shadows and the haze all
    // agree about where the sun is. Fog distances are re-tuned once the model's
    // real-world scale is known (see setSceneScaleFromModelWidth below).
    scene.fog = new THREE.Fog(0x9db6cf, 30, 420);
    skyRig = createSky(scene, 4200);

    // Create camera - extended far plane to prevent clipping
    camera = new THREE.PerspectiveCamera(
        45,
        viewportSize().width / viewportSize().height,
        0.01,
        5000
    );
    camera.position.set(50, 30, 50);

    // Create renderer - PREMIUM QUALITY SETTINGS
    renderer = new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: 'high-performance',
        alpha: false,
        stencil: false
    });
    renderer.setSize(viewportSize().width, viewportSize().height);
    // Phones get 1.0: at a 3x device ratio the capped 1.5 is nine times the
    // pixels per frame, which is the single biggest cost on mobile.
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, device.pixelRatio));

    // Shadow settings - optimized for performance
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Faster than VSM
    renderer.shadowMap.autoUpdate = false; // Manual update for performance

    // Color and tone mapping
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.LinearToneMapping;
    renderer.toneMappingExposure = Math.pow(2, devSettings.exposure);

    container.appendChild(renderer.domElement);

    // Setup pmndrs post-processing
    composer = new EffectComposer(renderer, {
        frameBufferType: THREE.HalfFloatType,
        multisampling: 0 // Disabled - using SMAA instead for anti-aliasing
    });

    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);

    // SSAO, depth of field and god rays are desktop-only. Each carries its own
    // half-float target, SSAO additionally draws the whole scene a second time
    // for normals, and on a phone they are the difference between a scene that
    // runs and a tab the browser kills. Bloom, vignette, anti-aliasing and tone
    // mapping stay everywhere: they are what the scene looks like.
    // See deviceProfile.ts.
    if (device.heavyPostFx) {
        // Normal Pass for SSAO
        const normalPass = new NormalPass(scene, camera);
        composer.addPass(normalPass);

        // SSAO Effect - Screen Space Ambient Occlusion
        ssaoEffect = new SSAOEffect(camera, normalPass.texture, {
            worldDistanceThreshold: 20,
            worldDistanceFalloff: 5,
            worldProximityThreshold: 0.4,
            worldProximityFalloff: 0.1,
            luminanceInfluence: 0.7,
            minRadiusScale: 0.33,
            radius: devSettings.ssaoRadius,
            intensity: devSettings.ssaoIntensity,
            bias: devSettings.ssaoBias,
            fade: 0.01,
            resolutionScale: 0.5, // Half resolution for performance
            samples: devSettings.ssaoSamples,
            rings: 4
        });
    }

    // Bloom Effect
    bloomEffect = new BloomEffect({
        intensity: devSettings.bloomIntensity,
        luminanceThreshold: devSettings.bloomLuminanceThreshold,
        luminanceSmoothing: devSettings.bloomLuminanceSmoothing,
        radius: devSettings.bloomRadius,
        mipmapBlur: true
    });

    // Vignette Effect
    vignetteEffect = new VignetteEffect({
        offset: devSettings.vignetteOffset,
        darkness: devSettings.vignetteDarkness
    });

    // SMAA Effect - using MEDIUM for better performance
    smaaEffect = new SMAAEffect({
        preset: SMAAPreset.MEDIUM
    });

    // Tone Mapping Effect
    toneMappingEffect = new ToneMappingEffect({
        mode: ToneMappingMode.LINEAR
    });

    if (device.heavyPostFx) {
        // Shafts of light off the sun disc. The light source is the sky's own
        // sun mesh, so the rays always agree with the shadows and the
        // atmosphere: all three read the same solar position.
        godRaysEffect = new GodRaysEffect(camera, skyRig.sun, {
            density: 0.92,
            decay: 0.93,
            weight: 0.32,
            exposure: 0.5,
            samples: 48,
            clampMax: 0.9,
            blur: true,
            kernelSize: KernelSize.SMALL,
            resolutionScale: 0.5
        });

        // Depth of field, focused on the model. Both distances are in world
        // units (the CoC shader measures true view-space distance), and this
        // scene's units are tiny, one metre is ~0.019, hence the small
        // numbers. Re-aimed every frame; see the focus block in animate().
        dofEffect = new DepthOfFieldEffect(camera, {
            focusDistance: 9,
            focusRange: 13,
            bokehScale: 2.1,
            resolutionScale: 0.5
        });
    }

    // Order matters: occlusion and defocus act on the scene, god rays and bloom
    // add light on top, tone mapping lands last before anti-aliasing. The
    // mobile chain is the same list minus whatever the profile skipped.
    const effectPass = new EffectPass(
        camera,
        ...[ssaoEffect, dofEffect, godRaysEffect].filter((e) => e !== null),
        bloomEffect,
        vignetteEffect,
        smaaEffect,
        toneMappingEffect
    );
    composer.addPass(effectPass);

    // Create orbit controls
    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enablePan = true;
    controls.enableZoom = true;
    controls.minDistance = 2;   // Allow closer zoom
    // Beyond ~45 units the sand disc's own rim comes into frame, which is the
    // one angle that gives the dressing away.
    controls.maxDistance = 45;
    controls.maxPolarAngle = Math.PI / 2 - 0.05;

    walk = createWalkMode({
        camera,
        controls,
        domElement: renderer.domElement,
        getModel: () => model,
        onExit: () => onWalkExit?.(),
    });

    // Raycaster for click-to-position
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    // Initialize hotspot container
    hotspotContainer = document.getElementById('hotspot-container') as HTMLElement;
    if (!hotspotContainer) {
        hotspotContainer = document.createElement('div');
        hotspotContainer.id = 'hotspot-container';
        document.body.appendChild(hotspotContainer);
    }

    // Double-click to create hotspot (in edit mode) or get 3D position
    renderer.domElement.addEventListener('dblclick', (event: MouseEvent) => {
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        raycaster.setFromCamera(mouse, camera);

        // Filter out hotspot meshes from intersection
        const intersectableObjects = scene.children.filter(child => {
            return !child.userData.hotspotId;
        });
        const intersects = raycaster.intersectObjects(intersectableObjects, true);

        if (intersects.length > 0) {
            const point = intersects[0].point;
            console.log('=== CLICKED POSITION ===');
            console.log(`X: ${point.x.toFixed(2)}, Y: ${point.y.toFixed(2)}, Z: ${point.z.toFixed(2)}`);

            // Create hotspot if in edit mode
            if (hotspotEditMode) {
                createHotspot(point);
            }
        }
    });

    // Lighting setup
    ambientLight = new THREE.AmbientLight(
        new THREE.Color(devSettings.ambientColor),
        devSettings.ambientIntensity
    );
    scene.add(ambientLight);

    // Key light: the sun. Position, colour and intensity are owned by the
    // day/night controller, which rides it around a fixed-radius arc so the
    // shadow frustum (sized for the model, not the city) always contains it.
    keyLight = new THREE.DirectionalLight(
        new THREE.Color(devSettings.directColor),
        devSettings.directIntensity
    );
    keyLight.position.set(5, 10, 7.5);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.width = device.shadowMapSize;
    keyLight.shadow.mapSize.height = device.shadowMapSize;
    keyLight.shadow.camera.near = 1;
    keyLight.shadow.camera.far = 140;
    keyLight.shadow.camera.left = -12;
    keyLight.shadow.camera.right = 12;
    keyLight.shadow.camera.top = 12;
    keyLight.shadow.camera.bottom = -12;
    keyLight.shadow.bias = -0.0004;
    keyLight.shadow.normalBias = 0.02;
    scene.add(keyLight);
    scene.add(keyLight.target);

    dayNight = new DayNightController({
        scene,
        sky: skyRig,
        key: keyLight,
        ambient: ambientLight,
        tiles: null,
    });
    dayNight.apply(sliderForNow());

    // Load environment map (HDR)
    const rgbeLoader = new RGBELoader();
    // quarry_01 (Poly Haven, CC0): warm rock and open sky, which is the light
    // Diriyah's escarpment actually sits in. Used for reflections and ambient
    // fill only, never as the visible backdrop, that is the procedural sky's
    // job, so the two cannot disagree about where the sun is.
    rgbeLoader.load('quarry_01_2k.hdr', (texture: THREE.DataTexture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;
        envMap = texture;
        scene.environment = envMap;
        scene.environmentIntensity = devSettings.envIntensity;
        // Also apply to apartment popup scene if it exists
        if (apartmentPopupScene) {
            apartmentPopupScene.environment = envMap;
            apartmentPopupScene.environmentIntensity = POPUP_ENV_INTENSITY;
        }
        // Note: skybox is set via CubeTextureLoader, not from envMap
        console.log('Environment map loaded: quarry_01_2k.hdr');
    });

    // Load GLTF model with Draco compression support
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');

    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load(
        'Diriyah-c.glb?v=' + Date.now(),
        (gltf) => {
            model = gltf.scene;

            // Center the model
            const box = new THREE.Box3().setFromObject(model);
            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());

            model.position.x = -center.x;
            model.position.y = -box.min.y;
            model.position.z = -center.z;
            
            const gpuInstancingGroups = new Map<string, Array<THREE.Mesh>>();

            // Enable shadows, frustum culling, and find lights from GLB
            model.traverse((child: THREE.Object3D) => {
                if ((child as THREE.Mesh).isMesh) {
                    const mesh = child as THREE.Mesh;
                    mesh.castShadow = true;
                    mesh.receiveShadow = true;
                    mesh.frustumCulled = true; // Enable frustum culling for performance

                    // Optimize geometry
                    if (mesh.geometry) {
                        mesh.geometry.computeBoundingSphere();
                        mesh.geometry.computeBoundingBox();
                    }

                    if (mesh.material) {
                        const mat = mesh.material as THREE.MeshStandardMaterial;
                        mat.shadowSide = THREE.FrontSide;
                        if (mat.envMapIntensity !== undefined) {
                            mat.envMapIntensity = 1.0;
                        }

                        const key = mesh.geometry.uuid + '_' + mat.uuid;
                        const group = gpuInstancingGroups.get(key);
                        if (group) {
                            group.push(mesh);
                        } else {
                            gpuInstancingGroups.set(key, [mesh]);
                        }
                    }
                }

                if ((child as THREE.Light).isLight) {
                    const light = child as THREE.Light;
                    console.log('Found light in GLB:', light.type, 'name:', light.name, 'intensity:', light.intensity);
                    glbLights.push(light);

                    if ((light as THREE.PointLight).isPointLight) {
                        const pointLight = light as THREE.PointLight;
                        glbPointLights.push(pointLight);
                        pointLight.castShadow = false; // Disable shadows for point lights
                        pointLight.decay = 2;
                        pointLight.distance = 0;
                    }

                    if ((light as THREE.SpotLight).isSpotLight) {
                        const spotLight = light as THREE.SpotLight;
                        glbSpotLights.push(spotLight);
                        spotLight.castShadow = false; // Disable shadows for spot lights
                        spotLight.penumbra = 0.5;
                        spotLight.decay = 2;
                    }

                    if ((light as THREE.DirectionalLight).isDirectionalLight) {
                        const dirLight = light as THREE.DirectionalLight;
                        dirLight.castShadow = true; // Only directional lights cast shadows
                        dirLight.shadow.mapSize.width = 2048;
                        dirLight.shadow.mapSize.height = 2048;
                        dirLight.shadow.camera.near = 0.01;
                        dirLight.shadow.camera.far = 2; // Small for AR model
                        dirLight.shadow.bias = -0.0001;
                        dirLight.shadow.normalBias = 0.02;
                        dirLight.shadow.camera.left = -50;
                        dirLight.shadow.camera.right = 50;
                        dirLight.shadow.camera.top = 50;
                        dirLight.shadow.camera.bottom = -50;
                    }
                }

                if ((child as THREE.Camera).isCamera) {
                    console.log('Found camera in GLB:', child.name, child.type);
                    glbCameras.push(child as THREE.Camera);
                }
            });

            // Scale up the model
            model.scale.set(5, 5, 5);
            // The alignment group is what the layout editor's nudge/rotate
            // moves: the model AND everything seated on it (pedestrians, the
            // plot's planting) ride together as one rigid body, while the
            // earth-glued layers — tiles, dunes, the desert town — stay put.
            // Without this, aligning the model left its own groves hanging in
            // the air where the terraces used to be.
            const aligned = new THREE.Group();
            aligned.name = 'model-alignment';
            scene.add(aligned);
            aligned.add(model);

            // Apply specific light settings
            applyLightSettings();

            // Recalculate size after scaling
            const scaledBox = new THREE.Box3().setFromObject(model);
            const scaledSize = scaledBox.getSize(new THREE.Vector3());

            console.log('Original model size:', size);
            console.log('Scaled model size:', scaledSize);
            console.log('Model center:', center);

            // The masterplan is a maquette; everything geo-referenced needs to
            // know how many scene units a real metre is worth. Derive it from
            // the model's own width against the site's real span, then turn the
            // model to match the plot (the tiles frame stays geo-aligned so the
            // sun keeps landing in the right part of the sky).
            const unitsPerMeter = setSceneScaleFromModelWidth(scaledSize.x);
            model.rotation.y = THREE.MathUtils.degToRad(MODEL_HEADING_DEG);
            // Haze now has a real distance to work with: fade the plateau out
            // over roughly 400 m to 12 km.
            // Haze starts beyond the masterplan, not across it: at 250 m the
            // far half of the model itself was being washed out, which reads as
            // a dirty render rather than as desert air.
            // Reaches 9 km: far enough that the photoreal plateau stays legible
            // out to the horizon, close enough to still read as desert air. The
            // backdrop range opts out of fog entirely and carries its own haze.
            if (scene.fog instanceof THREE.Fog) {
                scene.fog.near = 700 * unitsPerMeter;
                scene.fog.far = 9_000 * unitsPerMeter;
            }
            initGoogleTiles(scaledSize);

            // Set dressing, keyed off the same grade the tiles are levelled to
            // so sand, palms and terrain all meet at one height.
            decor = createDesertDecor({
                scene,
                baseUrl: 'plants/',
                groundY: modelPavingHeight() - m(TERRAIN_CLEARANCE_M),
                plotRadiusM: (0.5 * scaledSize.x) / unitsPerMeter,
                sampleGround: sampleTerrainHeight,
            });
            // The dressing is built after the first sun solve, so give the
            // backdrop its haze now rather than leaving it on its placeholder
            // tone until the next time the sun happens to move.
            applyTimeOfDay(dayNight.time);

            // People stand on the model's Base but live in the scene, in world
            // units (see pedestrians.ts for why that frame and not the model's).
            pedestrians = createPedestrians({
                baseUrl: 'people/',
                scene,
                model,
                plotHalfXM: (0.5 * scaledSize.x) / unitsPerMeter,
                plotHalfZM: (0.5 * scaledSize.z) / unitsPerMeter,
                count: device.pedestrians,
            });
            // People stand on the model, so they follow its alignment.
            const pedestrianGroup = scene.getObjectByName('pedestrians');
            if (pedestrianGroup) aligned.add(pedestrianGroup);
            if (decor) aligned.add(decor.plantingGroup);

            // Placement: the committed layout ships to every visitor; the
            // editor (behind ?layout=edit) builds against this context. The
            // WHOLE model is the target, not just its ground plane: the first
            // surface a ray meets wins, so items land on roofs and terraces
            // instead of falling through into interiors. The residence marker
            // volumes are excluded (they occupy air, not surface), as is the
            // pool water.
            const groundTargets: THREE.Object3D[] = [model];
            if (googleTiles) groundTargets.push(googleTiles.group);
            placementContext = {
                scene,
                camera,
                domElement: renderer.domElement,
                groundTargets,
                excludeNames: ['apartment-blocks', 'Water'],
                unitsPerMeter,
                model: aligned,
            };
            void loadLayout({
                scene,
                url: 'layouts/diriyah.json',
                unitsPerMeter,
                model: aligned,
            }).then(
                (runtime) => {
                    placementRuntime = runtime;
                },
            );

            gpuInstancingGroups.forEach((meshes) => {
                if (meshes.length < 2) return; // no benefit

                const base = meshes[0];
                const instanced = new THREE.InstancedMesh(
                    base.geometry,
                    base.material,
                    meshes.length
                );

                const dummy = new THREE.Object3D();

                meshes.forEach((m, i) => {
                    m.updateWorldMatrix(true, false);
                    dummy.matrix.copy(m.matrixWorld);
                    instanced.setMatrixAt(i, dummy.matrix);

                    m.parent?.remove(m);
                });

                scene.add(instanced);
            });

            // Free orbit is the default and only overview camera; the old
            // front/top/table presets are gone.
            unlockCamera();
            fitCameraToModel();
            populateGLBControls();

            // The loading overlay is a React component now; it fades and
            // unmounts itself off these two calls.
            onProgress?.(100);
            onLoaded?.();

            // Glowing residence volumes. Parented to the model so they inherit
            // its centring, scale and heading and therefore land exactly on the
            // buildings they mark, whatever those transforms become.
            apartmentBlocks = createApartmentBlocks({
                url: APARTMENT_BASE + 'Diriyah-AparmentBlocks.glb',
                parent: model,
                domElement: renderer.domElement,
                labelHost: hotspotContainer,
                camera,
                onSelect: (apartmentIndex) => {
                    // Reuse the existing hotspot record so the takeover keeps
                    // its title, description and media without a second source
                    // of truth for what a residence is.
                    const hotspot = hotspots.find((h) => h.apartmentIndex === apartmentIndex);
                    if (hotspot) openApartmentPopup(hotspot);
                },
            });

            // Start background preloading of apartments. Desktop only: on a
            // phone this is ~8 MB of GLB and its decoded geometry held for a
            // popup that may never be opened, on the device with the least
            // room for it. They still load on demand when one is tapped.
            if (device.preloadApartments) preloadApartments();

            // Create default hotspots
            createDefaultHotspots();

            // Update shadows once after model load (shadowMap.autoUpdate is false for performance)
            renderer.shadowMap.needsUpdate = true;
        },
        (xhr) => {
            // Fallback to known file size when server doesn't expose Content-Length
            // (chunked transfer / compressed responses leave xhr.total === 0).
            const MODEL_BYTES = 12_493_604;
            const total = xhr.total > 0 ? xhr.total : MODEL_BYTES;
            const percent = Math.min(Math.round((xhr.loaded / total) * 100), 100);
            onProgress?.(percent);
        },
        (error) => {
            console.error('Error loading model:', error);
            alert('Failed to load 3D model');
        }
    );

    window.addEventListener('resize', onWindowResize);
    // dat.gui is a developer surface, not part of the product chrome. It used
    // to be always-on because the vanilla build had no other way to reach these
    // knobs; now it is opt-in via ?dev.
    if (new URLSearchParams(location.search).has('dev')) initDevGUI();
    animate(0);
}

// Populate GLB camera buttons and light controls
function populateGLBControls(): void {
    if (!window.guiFolders) return;

    glbCameras.forEach((glbCam, index) => {
        const camName = glbCam.name || `Camera ${index + 1}`;
        const flyToFunc = () => flyToCamera(glbCam);
        window.guiFolders.cameras.add({ [camName]: flyToFunc }, camName).name(`Fly to: ${camName}`);
    });

    glbPointLights.forEach((light, index) => {
        const lightName = light.name || `Point Light ${index + 1}`;
        const folder = window.guiFolders.pointLights.addFolder(lightName);

        const lightSettings = {
            enabled: light.visible,
            intensity: light.intensity,
            color: '#' + light.color.getHexString(),
            distance: light.distance,
            decay: light.decay
        };

        folder.add(lightSettings, 'enabled').name('Enabled').onChange((v: boolean) => {
            light.visible = v;
        });
        folder.add(lightSettings, 'intensity', 0, 100).name('Intensity').onChange((v: number) => {
            light.intensity = v;
        });
        folder.addColor(lightSettings, 'color').name('Color').onChange((v: string) => {
            light.color.set(v);
        });
        folder.add(lightSettings, 'distance', 0, 100).name('Distance').onChange((v: number) => {
            light.distance = v;
        });
        folder.add(lightSettings, 'decay', 0, 5).name('Decay').onChange((v: number) => {
            light.decay = v;
        });
    });

    glbSpotLights.forEach((light, index) => {
        const lightName = light.name || `Spot Light ${index + 1}`;
        const folder = window.guiFolders.spotLights.addFolder(lightName);

        const lightSettings = {
            enabled: light.visible,
            intensity: light.intensity,
            color: '#' + light.color.getHexString(),
            distance: light.distance,
            angle: THREE.MathUtils.radToDeg(light.angle),
            penumbra: light.penumbra,
            decay: light.decay
        };

        folder.add(lightSettings, 'enabled').name('Enabled').onChange((v: boolean) => {
            light.visible = v;
        });
        folder.add(lightSettings, 'intensity', 0, 100).name('Intensity').onChange((v: number) => {
            light.intensity = v;
        });
        folder.addColor(lightSettings, 'color').name('Color').onChange((v: string) => {
            light.color.set(v);
        });
        folder.add(lightSettings, 'distance', 0, 100).name('Distance').onChange((v: number) => {
            light.distance = v;
        });
        folder.add(lightSettings, 'angle', 0, 90).name('Angle (deg)').onChange((v: number) => {
            light.angle = THREE.MathUtils.degToRad(v);
        });
        folder.add(lightSettings, 'penumbra', 0, 1).name('Penumbra').onChange((v: number) => {
            light.penumbra = v;
        });
        folder.add(lightSettings, 'decay', 0, 5).name('Decay').onChange((v: number) => {
            light.decay = v;
        });
    });

    console.log(`Added ${glbCameras.length} camera buttons, ${glbPointLights.length} point lights, ${glbSpotLights.length} spot lights to GUI`);
}

// Fly camera to a GLB camera position with smooth animation
function flyToCamera(targetCam: THREE.Camera, viewMode: 'free' | 'locked' | 'constrained' = 'free'): void {
    const targetPos = new THREE.Vector3();
    targetCam.getWorldPosition(targetPos);

    const targetDir = new THREE.Vector3(0, 0, -1);
    targetDir.applyQuaternion(targetCam.quaternion);

    const lookAtPoint = targetPos.clone().add(targetDir.multiplyScalar(10));

    const startPos = camera.position.clone();
    const startTarget = controls.target.clone();
    const duration = 1500;
    const startTime = performance.now();

    // Reset limits temporarily for the flight
    controls.minAzimuthAngle = -Infinity;
    controls.maxAzimuthAngle = Infinity;
    controls.minPolarAngle = 0;
    controls.maxPolarAngle = Math.PI;

    cameraLocked = viewMode !== 'free';

    function animateFly(currentTime: number): void {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const eased = progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        camera.position.lerpVectors(startPos, targetPos, eased);
        controls.target.lerpVectors(startTarget, lookAtPoint, eased);
        controls.update();

        if (progress < 1) {
            requestAnimationFrame(animateFly);
        } else {
            // Animation Complete - Apply constraints based on mode
            if (viewMode === 'locked') {
                // Top View Mode: No rotate/pan, Zoom only
                controls.enableRotate = false;
                controls.enablePan = false;
                controls.enableZoom = true;
                console.log('Camera controls locked (Top View mode)');
            }
            else if (viewMode === 'constrained') {
                // Table Mode: Orbit ±10 deg only, No zoom/pan
                controls.enableRotate = true;
                controls.enablePan = false;
                controls.enableZoom = false;

                // Lock angles based on current landing position
                const currentAzimuth = controls.getAzimuthalAngle();
                const currentPolar = controls.getPolarAngle();
                const limit = 10 * (Math.PI / 180); // 10 degrees in radians

                controls.minAzimuthAngle = currentAzimuth - limit;
                controls.maxAzimuthAngle = currentAzimuth + limit;

                // Lock vertical movement completely
                controls.minPolarAngle = currentPolar;
                controls.maxPolarAngle = currentPolar;

                console.log(`Camera constrained: ±10º azimuth, locked polar at ${currentPolar.toFixed(2)}`);
            }
        }
    }

    requestAnimationFrame(animateFly);
    console.log(`Flying to camera: ${targetCam.name}`, targetPos, `Mode: ${viewMode}`);
}

// Unlock camera controls and reset limits
function unlockCamera(): void {
    cameraLocked = false;
    controls.enableRotate = true;
    controls.enablePan = true;
    controls.enableZoom = true;

    // Reset limits to defaults
    controls.minAzimuthAngle = -Infinity;
    controls.maxAzimuthAngle = Infinity;
    controls.minPolarAngle = 0;
    controls.maxPolarAngle = Math.PI / 2 - 0.05; // Restore original vertical limit

    console.log('Camera controls unlocked');
}

// Apply specific light settings from user configuration
function applyLightSettings(): void {
    glbPointLights.forEach(light => {
        const name = light.name.toLowerCase();

        if (name.includes('purple')) {
            light.visible = true;
            light.intensity = 39;
            light.color.set('#4700ff');
            light.distance = 100;
            light.decay = 0;
            console.log('Applied purplelight settings:', light.name);
        } else if (name.includes('pink')) {
            light.visible = true;
            light.intensity = 7;
            light.color.set('#d400ff');
            light.distance = 0;
            light.decay = 0;
            console.log('Applied pinklight settings:', light.name);
        }
    });

    glbSpotLights.forEach(light => {
        const name = light.name.toLowerCase();

        if (name.includes('spot')) {
            light.visible = true;
            light.intensity = 24;
            light.color.set('#ffc4af');
            light.distance = 0;
            light.angle = THREE.MathUtils.degToRad(10);
            light.penumbra = 1;
            light.decay = 1.4;
            console.log('Applied spotlight settings:', light.name);
        }
    });

    console.log('Light settings applied');
}

// World Y of the masterplan's paved ground at its centre, which is what the
// Google terrain has to be levelled to. Measured off the model rather than
// assumed: the maquette's base group runs from roughly -15 m to +8 m around the
// origin, so "the ground" is nowhere near y=0 and levelling the tiles to zero
// leaves the buildings floating.
function modelPavingHeight(): number {
    if (!model) return 0;
    const box = new THREE.Box3().setFromObject(model);
    const probe = new THREE.Raycaster(
        new THREE.Vector3(0, box.max.y + 1, 0),
        new THREE.Vector3(0, -1, 0),
    );
    probe.far = box.max.y - box.min.y + 2;
    const base = model.getObjectByName('Base');
    const hit = probe.intersectObject(base ?? model, true)[0];
    // Falling back to the box centre keeps a renamed or missing Base group from
    // dropping the terrain to zero, which is the failure this function exists
    // to prevent.
    return hit ? hit.point.y : box.min.y + (box.max.y - box.min.y) * 0.5;
}

// Google Photorealistic 3D Tiles under the masterplan. Silently skipped when
// no Maps key is configured: the procedural sky and the model still work, the
// scene just has no real city around it.
function initGoogleTiles(modelSize: THREE.Vector3): void {
    if (!TILES_KEY) {
        console.info('No VITE_GOOGLE_MAPS_API_KEY set: skipping Diriyah 3D Tiles context.');
        return;
    }
    // Pad sized to the model's own footprint, so only the plot the masterplan
    // occupies is flattened and the surrounding plateau keeps its terrain.
    const padMeters = (0.5 * Math.hypot(modelSize.x, modelSize.z)) / sceneScale.unitsPerMeter;
    googleTiles = new GoogleTiles(TILES_KEY, camera, renderer, padMeters);
    // The tiles level themselves for as long as they keep streaming, and each
    // correction moves the whole tileset. The desert houses are raycast onto
    // that terrain and then left alone, so without this they keep the heights
    // they were seated at and end up buried once the terrain rises under them.
    googleTiles.onLevelChanged = () => decor?.resetTownSeating();
    googleTiles.setGroundLevel(modelPavingHeight() - m(TERRAIN_CLEARANCE_M));
    scene.add(googleTiles.group);
    dayNight.setTiles(googleTiles);
    dayNight.apply(dayNight.time);
    // Detail target tightens once the first tiles have had a moment to stream.
    setTimeout(() => googleTiles?.setInteractive(true), 4000);
}

// Real ground height at a point given in metres from the site, in world units.
// Returns null while the terrain there has not streamed in, which is what lets
// the houses wait for ground rather than guessing at it.
const terrainProbe = new THREE.Raycaster();
function sampleTerrainHeight(xM: number, zM: number): number | null {
    if (!googleTiles) return null;
    const scale = sceneScale.unitsPerMeter;
    terrainProbe.set(
        new THREE.Vector3(m(xM), googleTiles.groundLevelY + 4000 * scale, m(zM)),
        new THREE.Vector3(0, -1, 0),
    );
    terrainProbe.far = 10000 * scale;
    const hit = terrainProbe.intersectObject(googleTiles.group, true)[0];
    return hit ? hit.point.y : null;
}

// One entry point for the sun: keeps the shadow map in step, since the
// renderer runs with shadowMap.autoUpdate disabled for performance.
function applyTimeOfDay(t: number): void {
    if (!dayNight) return;
    dayNight.apply(t);
    // The distant range is unlit, so it only tracks the day through this: the
    // scene's own haze colour, which the controller has just set.
    if (decor && scene.fog instanceof THREE.Fog) decor.setHaze(scene.fog.color);
    // The desert houses are lit geometry sitting on tiles that dim themselves at
    // night, so they need the same fall-off or they glow against dark terrain.
    decor?.setDaylight(1 - dayNight.night);
    renderer.shadowMap.needsUpdate = true;
}

// Fit camera to model
function fitCameraToModel(): void {
    if (!model) return;

    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());

    const modelWidth = size.x;
    const modelHeight = size.y;

    const fovRad = camera.fov * (Math.PI / 180);
    const { width: viewWidth, height: viewHeight } = viewportSize();
    const aspect = viewWidth / viewHeight;
    const hFov = 2 * Math.atan(Math.tan(fovRad / 2) * aspect);
    const distanceToFitWidth = (modelWidth / 2) / Math.tan(hFov / 2);
    // 0.55 rather than 0.85: the masterplan is the subject, and pulling in this
    // far also keeps the set dressing and the coarsest Google tiles out toward
    // the frame edge where the depth of field can bury them.
    const finalDistance = distanceToFitWidth * 0.55;

    camera.position.set(
        finalDistance * 0.7,
        finalDistance * 0.5,
        finalDistance * 0.7
    );

    controls.target.set(0, modelHeight * 0.3, 0);
    controls.update();

    console.log('Model size:', size);
    console.log('Final distance:', finalDistance);
    console.log('Camera position:', camera.position);
}

// Initialize dev GUI
function initDevGUI(): void {
    gui = new dat.GUI({ width: 300 });
    gui.domElement.style.marginTop = '50px';

    // Renderer folder
    const rendererFolder = gui.addFolder('Renderer');
    rendererFolder.add(devSettings, 'exposure', -5, 5).name('Exposure (EV)').onChange((v: number) => {
        renderer.toneMappingExposure = Math.pow(2, v);
    });

    // Tone mapping options (postprocessing library modes)
    const toneMappingOptions: { [key: string]: ToneMappingMode } = {
        'Linear': ToneMappingMode.LINEAR,
        'Reinhard': ToneMappingMode.REINHARD,
        'Reinhard2': ToneMappingMode.REINHARD2,
        'Reinhard2 Adaptive': ToneMappingMode.REINHARD2_ADAPTIVE,
        'Uncharted2': ToneMappingMode.UNCHARTED2,
        'Optimized Cineon': ToneMappingMode.OPTIMIZED_CINEON,
        'ACES Filmic': ToneMappingMode.ACES_FILMIC,
        'AGX': ToneMappingMode.AGX,
        'Neutral': ToneMappingMode.NEUTRAL
    };
    rendererFolder.add(devSettings, 'toneMapping', Object.keys(toneMappingOptions)).name('Tone Mapping').onChange((v: string) => {
        toneMappingEffect.mode = toneMappingOptions[v];
    });

    // Lighting folder
    const lightFolder = gui.addFolder('Lighting');
    lightFolder.add(devSettings, 'ambientIntensity', 0, 2).name('Ambient Intensity').onChange((v: number) => {
        if (ambientLight) ambientLight.intensity = v;
    });
    lightFolder.add(devSettings, 'directIntensity', 0, 5).name('Direct Intensity').onChange((v: number) => {
        scene.traverse((child: THREE.Object3D) => {
            if ((child as THREE.DirectionalLight).isDirectionalLight && child !== ambientLight) {
                (child as THREE.DirectionalLight).intensity = v;
            }
        });
    });
    lightFolder.add(devSettings, 'punctualLights').name('Punctual Lights').onChange((v: boolean) => {
        glbLights.forEach(light => {
            light.visible = v;
        });
    });

    // Environment folder
    const envFolder = gui.addFolder('Environment (HDR)');
    envFolder.add(devSettings, 'envIntensity', 0, 3).name('Intensity').onChange((v: number) => {
        if (scene) scene.environmentIntensity = v;
    });
    envFolder.add(devSettings, 'showEnvBackground').name('Show as Background').onChange((v: boolean) => {
        if (scene && envMap) {
            scene.background = v ? envMap : new THREE.Color(0x191919);
        }
    });

    // Post-processing folder
    const postFolder = gui.addFolder('Post-Processing');

    postFolder.add(devSettings, 'bloomEnabled').name('Bloom').onChange((v: boolean) => {
        bloomEffect.blendMode.opacity.value = v ? 1 : 0;
    });
    postFolder.add(devSettings, 'bloomIntensity', 0, 3).name('Bloom Intensity').onChange((v: number) => {
        bloomEffect.intensity = v;
    });
    postFolder.add(devSettings, 'bloomLuminanceThreshold', 0, 1).name('Bloom Threshold').onChange((v: number) => {
        bloomEffect.luminanceMaterial.threshold = v;
    });
    postFolder.add(devSettings, 'bloomRadius', 0, 1).name('Bloom Radius').onChange((v: number) => {
        bloomEffect.mipmapBlurPass.radius = v;
    });

    postFolder.add(devSettings, 'vignetteEnabled').name('Vignette').onChange((v: boolean) => {
        vignetteEffect.blendMode.opacity.value = v ? 1 : 0;
    });
    postFolder.add(devSettings, 'vignetteOffset', 0, 1).name('Vignette Offset').onChange((v: number) => {
        vignetteEffect.offset = v;
    });
    postFolder.add(devSettings, 'vignetteDarkness', 0, 1).name('Vignette Darkness').onChange((v: number) => {
        vignetteEffect.darkness = v;
    });

    // SSAO Controls
    // Null on mobile, where the effect is never built.
    postFolder.add(devSettings, 'ssaoEnabled').name('SSAO').onChange((v: boolean) => {
        if (ssaoEffect) ssaoEffect.blendMode.opacity.value = v ? 1 : 0;
    });
    postFolder.add(devSettings, 'ssaoIntensity', 0, 5).name('SSAO Intensity').onChange((v: number) => {
        if (ssaoEffect) ssaoEffect.intensity = v;
    });
    postFolder.add(devSettings, 'ssaoRadius', 0.01, 1).name('SSAO Radius').onChange((v: number) => {
        if (ssaoEffect) ssaoEffect.radius = v;
    });
    // Note: SSAO bias is set at initialization and cannot be changed dynamically
    postFolder.add(devSettings, 'ssaoBias', 0, 0.1).name('SSAO Bias (restart required)');

    // Model folder
    const modelFolder = gui.addFolder('Model');
    modelFolder.add(devSettings, 'modelScale', 0.1, 5).onChange((v: number) => {
        if (model) {
            model.scale.set(v, v, v);
        }
    });

    // Top View folder
    const topViewFolder = gui.addFolder('Top View');
    topViewFolder.add(devSettings, 'topViewZoom', 0.1, 5).name('Zoom Multiplier').onChange((v: number) => {
        console.log('Top View Zoom set to:', v);
    });

    // Actions
    const actionsFolder = gui.addFolder('Actions');
    actionsFolder.add(devSettings, 'fitToView').name('Fit to View');
    actionsFolder.add(devSettings, 'logCamera').name('Log Camera to Console');

    // GLB Cameras folder
    const glbCameraFolder = gui.addFolder('GLB Cameras');

    // GLB Lights folders
    const pointLightsFolder = gui.addFolder('Point Lights');

    const spotLightsFolder = gui.addFolder('Spot Lights');

    window.guiFolders = {
        cameras: glbCameraFolder,
        pointLights: pointLightsFolder,
        spotLights: spotLightsFolder
    };
}

// Viewport size that is never zero.
//
// window.innerWidth/innerHeight can both report 0 while the page is still being
// laid out — reliably so inside an embedded preview pane. Every consumer here
// divides by them, so a zero pair produced NaN for the camera aspect, and a NaN
// camera silently takes the Google tiles down with it: the tile set cannot
// compute screen-space error from a NaN matrix, so nothing ever streams, the
// terrain never levels and no houses seat. Falling back to the last good size
// keeps one transient layout frame from poisoning the whole scene.
let lastViewport = { width: 1280, height: 720 };
function viewportSize(): { width: number; height: number } {
    const width = window.innerWidth;
    const height = window.innerHeight;
    if (width > 0 && height > 0) lastViewport = { width, height };
    return lastViewport;
}

function onWindowResize(): void {
    const { width, height } = viewportSize();
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
    composer.setSize(width, height);
}

// Hotspot functions
function createHotspot(
    position: THREE.Vector3,
    title: string = 'New Hotspot',
    description: string = '',
    apartmentIndex: number | null = null,
    mediaUrl: string = '',
    mediaType: 'image' | 'video' | null = null,
    color: string = defaultHotspotColor
): Hotspot {
    const id = 'hotspot-' + Date.now();

    // Create 3D marker - small sphere (scaled for the model size)
    const geometry = new THREE.SphereGeometry(0.15, 16, 16);
    const material = new THREE.MeshBasicMaterial({
        color: new THREE.Color(color),
        transparent: true,
        opacity: 0.95
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(position);
    mesh.userData.hotspotId = id;
    // The glowing residence volumes are the markers now. The hotspot record
    // stays as the data behind a residence (title, description, media, and the
    // link the takeover opens), but its sphere and HTML pin no longer draw.
    mesh.visible = false;
    scene.add(mesh);

    // Create HTML label with apartment indicator class if linked
    const hasApartmentClass = apartmentIndex !== null ? ' has-apartment' : '';
    // Determine apartment type label based on title
    let apartmentTypeLabel = '';
    if (title.toLowerCase().includes('penthouse') || title.toLowerCase() === 'ph') {
        apartmentTypeLabel = 'PH';
    } else if (title.toLowerCase().includes('3br') || title.toLowerCase().includes('3 br') || title.toLowerCase().includes('3-br')) {
        apartmentTypeLabel = '3BR';
    } else if (title.toLowerCase().includes('2br') || title.toLowerCase().includes('2 br') || title.toLowerCase().includes('2-br')) {
        apartmentTypeLabel = '2BR';
    } else if (title.toLowerCase().includes('1br') || title.toLowerCase().includes('1 br') || title.toLowerCase().includes('1-br')) {
        apartmentTypeLabel = '1BR';
    } else if (title.toLowerCase().includes('studio')) {
        apartmentTypeLabel = 'Studio';
    }
    const label = document.createElement('div');
    label.className = 'hotspot-label';
    label.innerHTML = `
        <div class="hotspot-marker${hasApartmentClass}" data-id="${id}" style="--hotspot-color: ${color};">
            <div class="hotspot-pulse"></div>
            <div class="hotspot-dot"></div>
            ${apartmentTypeLabel ? `<div class="hotspot-type-label">${apartmentTypeLabel}</div>` : ''}
        </div>
        <div class="hotspot-content" data-id="${id}">
            <div class="hotspot-title">${title}</div>
            <div class="hotspot-description">${description || 'Click to view'}</div>
        </div>
    `;
    label.dataset.id = id;
    // The label is built but no longer mounted: the residence volumes are the
    // visible, clickable markers now, and a pulsing pin AND a glowing block on
    // the same building read as two competing UIs. The record itself stays —
    // it is the source of truth the popup takeover reads its title,
    // description and media from — and a future placement/hotspot editor can
    // re-append the elements it wants to expose.

    const hotspot: Hotspot = {
        id,
        position: position.clone(),
        title,
        description,
        mesh,
        label,
        apartmentIndex,
        mediaUrl,
        mediaType,
        color
    };

    hotspots.push(hotspot);
    window.hotspots = hotspots;

    // Add click handler for editing or viewing apartment
    label.addEventListener('click', (e) => {
        e.stopPropagation();
        if (hotspotEditMode) {
            openHotspotEditor(hotspot);
        } else {
            // If linked to apartment, open popup; otherwise toggle content
            if (hotspot.apartmentIndex !== null && hotspot.apartmentIndex > 0) {
                openApartmentPopup(hotspot);
            } else {
                toggleHotspotContent(hotspot);
            }
        }
    });

    console.log('Hotspot created:', id, position, apartmentIndex ? `linked to apartment ${apartmentIndex}` : '');
    return hotspot;
}

function toggleHotspotContent(hotspot: Hotspot): void {
    const content = hotspot.label.querySelector('.hotspot-content') as HTMLElement;
    if (content) {
        content.classList.toggle('visible');
    }
}

function openHotspotEditor(hotspot: Hotspot): void {
    selectedHotspot = hotspot;
    const modal = document.getElementById('hotspot-editor-modal');
    const titleInput = document.getElementById('hotspot-title-input') as HTMLInputElement;
    const descInput = document.getElementById('hotspot-desc-input') as HTMLTextAreaElement;
    const apartmentSelect = document.getElementById('hotspot-apartment-select') as HTMLSelectElement;
    const mediaTypeSelect = document.getElementById('hotspot-media-type') as HTMLSelectElement;
    const mediaUrlInput = document.getElementById('hotspot-media-url') as HTMLInputElement;
    const mediaUrlGroup = document.getElementById('hotspot-media-url-group');
    const colorInput = document.getElementById('hotspot-color-input') as HTMLInputElement;

    if (modal && titleInput && descInput) {
        titleInput.value = hotspot.title;
        descInput.value = hotspot.description;

        // Populate apartment selector
        if (apartmentSelect) {
            apartmentSelect.value = hotspot.apartmentIndex !== null ? String(hotspot.apartmentIndex) : '';
        }

        // Populate media fields
        if (mediaTypeSelect) {
            mediaTypeSelect.value = hotspot.mediaType || '';
        }
        if (mediaUrlInput) {
            mediaUrlInput.value = hotspot.mediaUrl || '';
        }
        // Show/hide media URL field based on media type
        if (mediaUrlGroup) {
            mediaUrlGroup.style.display = hotspot.mediaType ? 'block' : 'none';
        }

        // Populate color picker
        if (colorInput) {
            colorInput.value = hotspot.color;
        }

        modal.classList.add('active');
    }
}

// Create default hotspots on load
function createDefaultHotspots(): void {
    const defaultHotspots = [
        {
            position: new THREE.Vector3(2.352564220875048, 0.7396898017603803, -0.9371936732990858),
            title: "2BR Apartment",
            description: "",
            apartmentIndex: 1
        },
        {
            position: new THREE.Vector3(-0.6030599623687468, 0.6491925689126096, -0.8081463721458957),
            title: "Penthouse",
            description: "",
            apartmentIndex: 3
        },
        {
            position: new THREE.Vector3(-1.7179654457068243, 0.6009500760953009, -0.47388252693720295),
            title: "3BR",
            description: "",
            apartmentIndex: 2
        }
    ];

    defaultHotspots.forEach(h => {
        createHotspot(h.position, h.title, h.description, h.apartmentIndex, '', null);
    });

    console.log('Default hotspots created:', defaultHotspots.length);
}

function updateHotspot(
    hotspot: Hotspot,
    title: string,
    description: string,
    apartmentIndex: number | null = null,
    mediaUrl: string = '',
    mediaType: 'image' | 'video' | null = null,
    color: string = hotspot.color
): void {
    hotspot.title = title;
    hotspot.description = description;
    hotspot.apartmentIndex = apartmentIndex;
    hotspot.mediaUrl = mediaUrl;
    hotspot.mediaType = mediaType;
    hotspot.color = color;

    const titleEl = hotspot.label.querySelector('.hotspot-title');
    const descEl = hotspot.label.querySelector('.hotspot-description');
    const marker = hotspot.label.querySelector('.hotspot-marker') as HTMLElement;

    if (titleEl) titleEl.textContent = title;
    if (descEl) descEl.textContent = description || 'Click to view';

    // Update marker visual indicator for apartment link and color
    if (marker) {
        marker.classList.toggle('has-apartment', apartmentIndex !== null);
        marker.style.setProperty('--hotspot-color', color);
    }

    // Update 3D mesh color
    const meshMaterial = hotspot.mesh.material as THREE.MeshBasicMaterial;
    meshMaterial.color.set(color);
}

function deleteHotspot(hotspot: Hotspot): void {
    scene.remove(hotspot.mesh);
    hotspot.label.remove();
    hotspots = hotspots.filter(h => h.id !== hotspot.id);
    window.hotspots = hotspots;
    console.log('Hotspot deleted:', hotspot.id);
}

// Unused while the pins are unmounted (see createHotspot); kept for the
// editor surface that will re-mount them.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function updateHotspotLabels(): void {
    hotspots.forEach(hotspot => {
        const screenPos = hotspot.position.clone().project(camera);

        // Check if hotspot is in front of camera
        const isBehind = screenPos.z > 1;

        if (isBehind) {
            hotspot.label.style.display = 'none';
            return;
        }

        hotspot.label.style.display = 'block';

        const x = (screenPos.x * 0.5 + 0.5) * window.innerWidth;
        const y = (-screenPos.y * 0.5 + 0.5) * window.innerHeight;

        hotspot.label.style.left = `${x}px`;
        hotspot.label.style.top = `${y}px`;

        // Scale based on distance
        const distance = camera.position.distanceTo(hotspot.position);
        const scale = Math.max(0.5, Math.min(1.5, 50 / distance));
        hotspot.label.style.transform = `translate(-50%, -50%) scale(${scale})`;
    });
}

function toggleHotspotEditMode(): void {
    hotspotEditMode = !hotspotEditMode;
    window.hotspotEditMode = hotspotEditMode;

    const editBtn = document.getElementById('hotspot-edit-btn');
    const indicator = document.getElementById('edit-mode-indicator');

    if (editBtn) {
        editBtn.classList.toggle('active', hotspotEditMode);
    }
    if (indicator) {
        indicator.classList.toggle('visible', hotspotEditMode);
    }

    // Update cursor
    if (renderer) {
        renderer.domElement.style.cursor = hotspotEditMode ? 'crosshair' : 'grab';
    }

    console.log('Hotspot edit mode:', hotspotEditMode ? 'ON' : 'OFF');
}

function exportHotspots(): string {
    const data = hotspots.map(h => ({
        id: h.id,
        position: { x: h.position.x, y: h.position.y, z: h.position.z },
        title: h.title,
        description: h.description,
        apartmentIndex: h.apartmentIndex,
        mediaUrl: h.mediaUrl,
        mediaType: h.mediaType,
        color: h.color
    }));
    return JSON.stringify(data, null, 2);
}

function importHotspots(jsonData: string): void {
    try {
        const data = JSON.parse(jsonData);
        data.forEach((item: any) => {
            const pos = new THREE.Vector3(item.position.x, item.position.y, item.position.z);
            createHotspot(
                pos,
                item.title,
                item.description,
                item.apartmentIndex ?? null,
                item.mediaUrl ?? '',
                item.mediaType ?? null,
                item.color ?? defaultHotspotColor
            );
        });
        console.log('Imported', data.length, 'hotspots');
    } catch (e) {
        console.error('Failed to import hotspots:', e);
    }
}

// Apartment model functions
function animateModelOut(targetModel: THREE.Group, onComplete: () => void): void {
    const duration = 800;
    const startTime = performance.now();
    const startScale = targetModel.scale.clone();
    const startOpacity = 1;

    // Make materials transparent for fade
    targetModel.traverse((child: THREE.Object3D) => {
        if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            if (mesh.material) {
                const mat = mesh.material as THREE.MeshStandardMaterial;
                mat.transparent = true;
            }
        }
    });

    function animateOut(currentTime: number): void {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out cubic
        const eased = 1 - Math.pow(1 - progress, 3);

        // Scale down and fade out
        const scale = startScale.x * (1 - eased * 0.3);
        targetModel.scale.set(scale, scale, scale);

        // Fade opacity
        const opacity = startOpacity * (1 - eased);
        targetModel.traverse((child: THREE.Object3D) => {
            if ((child as THREE.Mesh).isMesh) {
                const mesh = child as THREE.Mesh;
                if (mesh.material) {
                    const mat = mesh.material as THREE.MeshStandardMaterial;
                    mat.opacity = opacity;
                }
            }
        });

        if (progress < 1) {
            requestAnimationFrame(animateOut);
        } else {
            targetModel.visible = false;
            onComplete();
        }
    }

    requestAnimationFrame(animateOut);
}

function animateModelIn(targetModel: THREE.Group, targetScale: number): void {
    const duration = 800;
    const startTime = performance.now();
    targetModel.visible = true;
    targetModel.scale.set(0.1, 0.1, 0.1);

    // Set initial opacity to 0
    targetModel.traverse((child: THREE.Object3D) => {
        if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            if (mesh.material) {
                const mat = mesh.material as THREE.MeshStandardMaterial;
                mat.transparent = true;
                mat.opacity = 0;
            }
        }
    });

    function animateIn(currentTime: number): void {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out back for bouncy effect
        const c1 = 1.70158;
        const c3 = c1 + 1;
        const eased = 1 + c3 * Math.pow(progress - 1, 3) + c1 * Math.pow(progress - 1, 2);

        // Scale up
        const scale = targetScale * eased;
        targetModel.scale.set(scale, scale, scale);

        // Fade in opacity
        const opacity = Math.min(progress * 1.5, 1);
        targetModel.traverse((child: THREE.Object3D) => {
            if ((child as THREE.Mesh).isMesh) {
                const mesh = child as THREE.Mesh;
                if (mesh.material) {
                    const mat = mesh.material as THREE.MeshStandardMaterial;
                    mat.opacity = opacity;
                }
            }
        });

        if (progress < 1) {
            requestAnimationFrame(animateIn);
        } else {
            // Reset opacity to 1 and disable transparency if not needed
            targetModel.traverse((child: THREE.Object3D) => {
                if ((child as THREE.Mesh).isMesh) {
                    const mesh = child as THREE.Mesh;
                    if (mesh.material) {
                        const mat = mesh.material as THREE.MeshStandardMaterial;
                        mat.opacity = 1;
                    }
                }
            });
            isTransitioning = false;
            updateApartmentLoadingState(false);
        }
    }

    requestAnimationFrame(animateIn);
}

function loadApartmentModel(apartment: ApartmentModel): void {
    if (isTransitioning || !apartment.file) return;

    isTransitioning = true;
    updateApartmentLoadingState(true);

    // If apartment is already loaded, just show it
    if (apartment.loaded && apartment.model) {
        showApartmentModel(apartment);
        return;
    }

    // Load the apartment model
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');

    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load(
        apartment.file,
        (gltf) => {
            const aptModel = gltf.scene;

            // Center the apartment model
            const box = new THREE.Box3().setFromObject(aptModel);
            const center = box.getCenter(new THREE.Vector3());

            aptModel.position.x = -center.x;
            aptModel.position.y = -box.min.y;
            aptModel.position.z = -center.z;

            aptModel.traverse((child: THREE.Object3D) => {
                if ((child as THREE.Mesh).isMesh) {
                    const mesh = child as THREE.Mesh;
                    mesh.castShadow = true;
                    mesh.receiveShadow = true;
                    if (mesh.material) {
                        (mesh.material as THREE.MeshStandardMaterial).shadowSide = THREE.FrontSide;
                    }
                }
            });

            // Scale to match main model (apartments might need different scaling)
            aptModel.scale.set(5, 5, 5);
            aptModel.visible = false;

            apartment.model = aptModel;
            apartment.loaded = true;

            scene.add(aptModel);
            console.log('Apartment model loaded:', apartment.name);

            showApartmentModel(apartment);
        },
        (xhr) => {
            const percent = xhr.total > 0 ? Math.min(Math.round((xhr.loaded / xhr.total) * 100), 100) : 0;
            console.log(`Loading ${apartment.name}: ${percent}%`);
        },
        (error) => {
            console.error('Error loading apartment model:', error);
            isTransitioning = false;
            updateApartmentLoadingState(false);
        }
    );
}

function showApartmentModel(apartment: ApartmentModel): void {
    // Hide current model (main or apartment) with animation
    if (currentApartment && currentApartment.model) {
        animateModelOut(currentApartment.model, () => {
            if (apartment.model) {
                animateModelIn(apartment.model, 5);
            }
        });
    } else if (model && model.visible) {
        animateModelOut(model, () => {
            if (apartment.model) {
                animateModelIn(apartment.model, 5);
            }
        });
    } else if (apartment.model) {
        animateModelIn(apartment.model, 5);
    }

    currentApartment = apartment;
}

function showMainModel(): void {
    if (isTransitioning) return;

    isTransitioning = true;
    updateApartmentLoadingState(true);

    // Hide current apartment with animation
    if (currentApartment && currentApartment.model) {
        animateModelOut(currentApartment.model, () => {
            // Show main model
            model.visible = true;
            animateModelIn(model, 5);
        });
    } else {
        model.visible = true;
        animateModelIn(model, 5);
    }

    currentApartment = null;
}

function switchApartment(index: number): void {
    const apartment = apartmentModels[index];
    if (!apartment) return;

    console.log('Switching to:', apartment.name);

    if (index === 0) {
        // Switch back to main model
        showMainModel();
    } else {
        loadApartmentModel(apartment);
    }
}

function updateApartmentLoadingState(loading: boolean): void {
    const selector = document.getElementById('apartment-selector') as HTMLSelectElement;
    const loadingIndicator = document.getElementById('apartment-loading');

    if (selector) {
        selector.disabled = loading;
    }
    if (loadingIndicator) {
        loadingIndicator.style.display = loading ? 'block' : 'none';
    }
}

// ============================================
// Apartment Popup System
// ============================================

let apartmentPopupAnimationId: number | null = null;
let preloadedApartmentModels: Map<number, THREE.Group> = new Map();

function initApartmentPopupRenderer(): void {
    const container = document.getElementById('apartment-popup-canvas');
    if (!container) return;

    // Create scene
    apartmentPopupScene = new THREE.Scene();
    apartmentPopupScene.background = new THREE.Color(0x191919);
    if (envMap) {
        apartmentPopupScene.environment = envMap;
        apartmentPopupScene.environmentIntensity = POPUP_ENV_INTENSITY;
    }

    // Create camera - closer initial position for larger model view
    apartmentPopupCamera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.1, 1000);
    apartmentPopupCamera.position.set(8, 5, 8);

    // Create renderer
    apartmentPopupRenderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        powerPreference: 'high-performance'
    });
    apartmentPopupRenderer.setSize(container.clientWidth, container.clientHeight);
    apartmentPopupRenderer.setPixelRatio(Math.min(window.devicePixelRatio, device.mobile ? 1 : 2));
    apartmentPopupRenderer.toneMapping = THREE.AgXToneMapping;
    apartmentPopupRenderer.toneMappingExposure = 1; // Exposure value of 1
    apartmentPopupRenderer.shadowMap.enabled = true;
    apartmentPopupRenderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(apartmentPopupRenderer.domElement);

    // Create controls - allow closer zoom for larger view
    apartmentPopupControls = new OrbitControls(apartmentPopupCamera, apartmentPopupRenderer.domElement);
    apartmentPopupControls.enableDamping = true;
    apartmentPopupControls.dampingFactor = 0.05;
    apartmentPopupControls.minDistance = 2;
    apartmentPopupControls.maxDistance = 50;
    apartmentPopupControls.maxPolarAngle = Math.PI / 2;
    apartmentPopupControls.target.set(0, 2, 0);

    // Add lights - brighter for interior apartment view
    // Interiors were lit three times over: a bright outdoor environment at 2.0
    // PLUS a full-strength ambient PLUS a 2.5 key. Everything pale clipped to
    // white and the plan lost its shading, which read as "the textures are
    // missing" even after the finishes were corrected. One budget instead:
    // environment for fill, a single key for shape, ambient only to keep the
    // shadow side from going black.
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.28);
    apartmentPopupScene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xfff3e2, 1.9);
    directionalLight.position.set(10, 20, 10);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    directionalLight.shadow.camera.near = 0.01;
    directionalLight.shadow.camera.far = 2; // Small for AR model
    apartmentPopupScene.add(directionalLight);

    // Setup post-processing with SSAO and AgX tonemapping
    apartmentPopupComposer = new EffectComposer(apartmentPopupRenderer);

    // Render pass
    const popupRenderPass = new RenderPass(apartmentPopupScene, apartmentPopupCamera);
    apartmentPopupComposer.addPass(popupRenderPass);

    // Normal pass for SSAO. Skipped on mobile for the same reason as the main
    // scene, and it matters more here: the popup is a SECOND WebGL context
    // alongside the masterplan's, on the device with the least headroom.
    const popupSSAOEffect = device.heavyPostFx ? (() => {
    const popupNormalPass = new NormalPass(apartmentPopupScene, apartmentPopupCamera);
    apartmentPopupComposer!.addPass(popupNormalPass);

    return new SSAOEffect(apartmentPopupCamera, popupNormalPass.texture, {
        worldDistanceThreshold: 20,
        worldDistanceFalloff: 5,
        worldProximityThreshold: 0.4,
        worldProximityFalloff: 0.1,
        luminanceInfluence: 0.7,
        minRadiusScale: 0.33,
        radius: 0.05,
        intensity: 2.0,
        bias: 0.025,
        fade: 0.01,
        resolutionScale: 0.5,
        samples: 16,
        rings: 4
    });
    })() : null;

    // Bloom effect
    const popupBloomEffect = new BloomEffect({
        intensity: 0.3,
        luminanceThreshold: 0.85,
        luminanceSmoothing: 0.4
    });

    // Tone mapping with AgX
    const popupToneMappingEffect = new ToneMappingEffect({
        mode: ToneMappingMode.AGX
    });

    // Add effects pass with SSAO
    apartmentPopupComposer.addPass(
        new EffectPass(
            apartmentPopupCamera,
            ...[popupSSAOEffect].filter((e) => e !== null),
            popupBloomEffect,
            popupToneMappingEffect,
        ),
    );

    console.log('Apartment popup renderer initialized');
}

function loadApartmentForPopup(apartmentIndex: number, callback: () => void): void {
    if (!apartmentPopupScene) return;

    const loadingEl = document.getElementById('apartment-popup-loading');
    if (loadingEl) loadingEl.classList.add('active');

    // Check if already preloaded
    if (preloadedApartmentModels.has(apartmentIndex)) {
        const cachedModel = preloadedApartmentModels.get(apartmentIndex)!;
        const clonedModel = cachedModel.clone();
        clonedModel.visible = true;
        apartmentPopupScene.add(clonedModel);
        if (loadingEl) loadingEl.classList.remove('active');
        console.log('Using preloaded apartment model:', apartmentIndex);
        // Fit camera to model for 80% fill
        fitApartmentPopupCamera(clonedModel);
        callback();
        return;
    }

    // Get apartment data
    const apartment = apartmentModels[apartmentIndex];
    if (!apartment || !apartment.file) {
        console.error('Invalid apartment index:', apartmentIndex);
        if (loadingEl) loadingEl.classList.remove('active');
        return;
    }

    // Load model
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');

    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load(
        apartment.file,
        (gltf) => {
            const aptModel = gltf.scene;

            // Center the model
            const box = new THREE.Box3().setFromObject(aptModel);
            const center = box.getCenter(new THREE.Vector3());
            aptModel.position.x = -center.x;
            aptModel.position.y = -box.min.y;
            aptModel.position.z = -center.z;

            aptModel.traverse((child: THREE.Object3D) => {
                if ((child as THREE.Mesh).isMesh) {
                    const mesh = child as THREE.Mesh;
                    mesh.castShadow = true;
                    mesh.receiveShadow = true;
                }
            });

            aptModel.scale.set(5, 5, 5);
            apartmentPopupScene!.add(aptModel);

            // Ensure environment map is applied to popup scene
            if (envMap && apartmentPopupScene) {
                apartmentPopupScene.environment = envMap;
                apartmentPopupScene.environmentIntensity = POPUP_ENV_INTENSITY;
            }

            if (loadingEl) loadingEl.classList.remove('active');
            console.log('Apartment model loaded for popup:', apartment.name);
            // Fit camera to model for 80% fill
            fitApartmentPopupCamera(aptModel);
            callback();
        },
        (xhr) => {
            const percent = xhr.total > 0 ? Math.min(Math.round((xhr.loaded / xhr.total) * 100), 100) : 0;
            console.log(`Loading popup apartment: ${percent}%`);
        },
        (error) => {
            console.error('Error loading apartment for popup:', error);
            if (loadingEl) loadingEl.classList.remove('active');
        }
    );
}

// Fit camera to apartment model so it fills ~80% of the view
function fitApartmentPopupCamera(model: THREE.Object3D): void {
    if (!apartmentPopupCamera || !apartmentPopupControls) return;

    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    // Get the max dimension for fitting
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = apartmentPopupCamera.fov * (Math.PI / 180);

    // Calculate distance to fill ~80% of view (0.8 factor means closer)
    let cameraDistance = maxDim / (2 * Math.tan(fov / 2));
    cameraDistance *= 0.6; // Bring camera closer to fill 80%

    // Position camera at an angle for nice 3D view
    const angle = Math.PI / 4; // 45 degrees
    apartmentPopupCamera.position.set(
        center.x + cameraDistance * Math.sin(angle),
        center.y + cameraDistance * 0.5,
        center.z + cameraDistance * Math.cos(angle)
    );

    // Update controls target to model center
    apartmentPopupControls.target.set(center.x, center.y, center.z);
    apartmentPopupControls.update();

    console.log('Apartment camera fitted to model, distance:', cameraDistance);
}

function animateApartmentPopup(): void {
    if (!apartmentPopupActive) return;

    apartmentPopupAnimationId = requestAnimationFrame(animateApartmentPopup);

    if (apartmentPopupControls) {
        apartmentPopupControls.update();
    }

    if (apartmentPopupComposer) {
        apartmentPopupComposer.render();
    }
}

function openApartmentPopup(hotspot: Hotspot): void {
    if (!hotspot.apartmentIndex) return;

    currentPopupHotspot = hotspot;
    apartmentPopupActive = true;
    mainRenderPaused = true;

    // Hide main model for performance
    if (model) model.visible = false;
    if (currentApartment && currentApartment.model) {
        currentApartment.model.visible = false;
    }

    // Show popup modal
    const modal = document.getElementById('apartment-popup-modal');
    if (modal) {
        modal.classList.add('active');
    }

    // Update popup info
    const titleEl = document.getElementById('apartment-popup-title');
    const descEl = document.getElementById('apartment-popup-description');
    const mediaContainer = document.getElementById('apartment-popup-media');

    if (titleEl) titleEl.textContent = hotspot.title;
    if (descEl) descEl.textContent = hotspot.description || 'Explore this beautiful apartment in 3D.';

    // Handle media
    if (mediaContainer) {
        mediaContainer.innerHTML = '';
        mediaContainer.classList.remove('has-media');

        if (hotspot.mediaType && hotspot.mediaUrl) {
            mediaContainer.classList.add('has-media');
            if (hotspot.mediaType === 'image') {
                const img = document.createElement('img');
                img.src = hotspot.mediaUrl;
                img.alt = hotspot.title;
                mediaContainer.appendChild(img);
            } else if (hotspot.mediaType === 'video') {
                const video = document.createElement('video');
                video.src = hotspot.mediaUrl;
                video.controls = true;
                video.autoplay = false;
                mediaContainer.appendChild(video);
            }
        }
    }

    // Initialize renderer if not done
    if (!apartmentPopupRenderer) {
        initApartmentPopupRenderer();
    }

    // Load apartment model
    loadApartmentForPopup(hotspot.apartmentIndex, () => {
        // Start animation loop
        animateApartmentPopup();
    });

    console.log('Opened apartment popup for:', hotspot.title);
}

function closeApartmentPopup(): void {
    apartmentPopupActive = false;
    mainRenderPaused = false;
    currentPopupHotspot = null;

    // Stop animation loop
    if (apartmentPopupAnimationId) {
        cancelAnimationFrame(apartmentPopupAnimationId);
        apartmentPopupAnimationId = null;
    }

    // Hide popup modal
    const modal = document.getElementById('apartment-popup-modal');
    if (modal) {
        modal.classList.remove('active');
    }

    // Clear popup scene (remove models, keep lights)
    if (apartmentPopupScene) {
        const toRemove: THREE.Object3D[] = [];
        apartmentPopupScene.traverse((child) => {
            if (child instanceof THREE.Group || (child as THREE.Mesh).isMesh) {
                // Don't remove lights
                if (!(child instanceof THREE.Light)) {
                    toRemove.push(child);
                }
            }
        });
        toRemove.forEach((obj) => {
            if (obj.parent === apartmentPopupScene) {
                apartmentPopupScene!.remove(obj);
                // Dispose geometry and materials
                if ((obj as THREE.Mesh).geometry) {
                    (obj as THREE.Mesh).geometry.dispose();
                }
                if ((obj as THREE.Mesh).material) {
                    const mat = (obj as THREE.Mesh).material;
                    if (Array.isArray(mat)) {
                        mat.forEach(m => m.dispose());
                    } else {
                        mat.dispose();
                    }
                }
            }
        });
    }

    // Show main model again
    if (currentApartment && currentApartment.model) {
        currentApartment.model.visible = true;
    } else if (model) {
        model.visible = true;
    }

    console.log('Closed apartment popup');
}

function preloadApartments(): void {
    console.log('Starting background apartment preloading...');

    const loadNext = (index: number) => {
        if (index >= apartmentModels.length) {
            console.log('All apartments preloaded');
            return;
        }

        const apartment = apartmentModels[index];
        if (!apartment.file || index === 0) {
            // Skip main model (index 0) and invalid entries
            loadNext(index + 1);
            return;
        }

        // Use requestIdleCallback for non-blocking loading
        const loadFunc = () => {
            const dracoLoader = new DRACOLoader();
            dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');

            const loader = new GLTFLoader();
            loader.setDRACOLoader(dracoLoader);

            loader.load(
                apartment.file!,
                (gltf) => {
                    const aptModel = gltf.scene;

                    // Center the model
                    const box = new THREE.Box3().setFromObject(aptModel);
                    const center = box.getCenter(new THREE.Vector3());
                    aptModel.position.x = -center.x;
                    aptModel.position.y = -box.min.y;
                    aptModel.position.z = -center.z;

                            aptModel.traverse((child: THREE.Object3D) => {
                        if ((child as THREE.Mesh).isMesh) {
                            const mesh = child as THREE.Mesh;
                            mesh.castShadow = true;
                            mesh.receiveShadow = true;
                        }
                    });

                    aptModel.scale.set(5, 5, 5);
                    aptModel.visible = false;

                    preloadedApartmentModels.set(index, aptModel);
                    console.log('Preloaded apartment:', apartment.name);

                    // Load next after delay
                    setTimeout(() => loadNext(index + 1), 1000);
                },
                () => {}, // Progress callback (empty function to avoid error)
                (error) => {
                    console.warn('Failed to preload apartment:', apartment.name, error);
                    setTimeout(() => loadNext(index + 1), 500);
                }
            );
        };

        if ('requestIdleCallback' in window) {
            (window as any).requestIdleCallback(loadFunc, { timeout: 5000 });
        } else {
            setTimeout(loadFunc, 100);
        }
    };

    // Start preloading after 3 seconds
    setTimeout(() => loadNext(1), 3000);
}

// Handle popup resize
function handlePopupResize(): void {
    if (!apartmentPopupActive || !apartmentPopupRenderer || !apartmentPopupCamera || !apartmentPopupComposer) return;

    const container = document.getElementById('apartment-popup-canvas');
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    apartmentPopupCamera.aspect = width / height;
    apartmentPopupCamera.updateProjectionMatrix();

    apartmentPopupRenderer.setSize(width, height);
    apartmentPopupComposer.setSize(width, height);
}

window.addEventListener('resize', handlePopupResize);

// Frame limiting for performance
let lastFrameTime = 0;
const targetFPS = 60;
const frameInterval = 1000 / targetFPS;

function animate(time: number): void {
    if (disposed) return;
    rafHandle = requestAnimationFrame(animate);

    // Skip rendering when apartment popup is active
    if (mainRenderPaused) return;

    // Frame rate limiting
    const deltaTime = time - lastFrameTime;
    if (deltaTime < frameInterval) return;
    lastFrameTime = time - (deltaTime % frameInterval);

    // Seconds since the previous rendered frame, for the environment layers.
    const dt = lastEnvTime ? Math.min((time - lastEnvTime) / 1000, 0.25) : 0;
    lastEnvTime = time;

    // Walk mode drives the camera itself; OrbitControls is disabled while it is
    // active, so updating it would fight the walker for the same transform.
    if (walk?.active) walk.update(dt);
    else controls.update();

    skyRig?.update(dt);
    googleTiles?.update(dt);
    apartmentBlocks?.update(dt, camera);
    decor?.update(dt);
    pedestrians?.update(dt);
    // Lantern light pool chases the camera; night level comes from the sun.
    placementRuntime?.update(camera);
    placementRuntime?.setNight(dayNight ? dayNight.night : 1);

    // Focus follows the subject: the orbit target in overview, and a fixed short
    // range ahead while walking, where a target-based focus would sit on the
    // walker's own feet.
    if (dofEffect) {
        const focus = walk?.active
            ? m(20)
            : Math.max(camera.position.distanceTo(controls.target), m(4));
        dofEffect.cocMaterial.focusDistance = focus;
        // The range has to cover the whole subject, not just the focal plane.
        // The masterplan is ~8 units across, so a range tighter than its own
        // radius defocuses the far half of the model that the shot is about;
        // 1.6x the viewing distance keeps the model crisp and lets the blur
        // build over the kilometres of terrain behind it.
        dofEffect.cocMaterial.focusRange = focus * 1.15;
    }

    // Live mode follows the site's wall clock. Re-solving the sun once a minute
    // is plenty: it moves a quarter of a degree in that time.
    if (liveTime && dayNight) {
        liveTimeTimer -= dt;
        if (liveTimeTimer <= 0) {
            liveTimeTimer = 60;
            applyTimeOfDay(sliderForNow());
        }
    }

    // Update hotspot label positions (throttled - every other frame)
    if (Math.floor(time / 32) % 2 === 0) {
    }

    composer.render();
}

// ---- React entry point ---------------------------------------------------
//
// The UI lives in React now (see src/App.tsx), so this file's job ends at the
// 3D layer plus the ViewerApi the chrome drives it through. That API is not new
// — the vanilla chrome already talked to the viewer through exactly this
// surface, which is what made the port a move rather than a rewrite.
//
// The apartment popup and hotspot labels still resolve their DOM by id, because
// they are positioned per frame from 3D and re-rendering them through React at
// 60fps would cost more than it buys. React renders the markup and owns the
// styling; this file reaches in and drives it.

export interface ViewerCallbacks {
    /** Model load progress, 0-100. */
    onProgress?: (percent: number) => void;
    onLoaded?: () => void;
    /** Walking can end without the UI asking: Esc, or a dropped pointer lock. */
    onWalkEnded?: () => void;
}

export interface ViewerHandle {
    setTimeOfDay: (t: number) => void;
    setLiveTime: (on: boolean) => void;
    setMarkers: (on: boolean) => void;
    setResidenceFilter: (apartmentIndex: number | null) => void;
    setBloom: (enabled: boolean) => void;
    enterWalk: () => boolean;
    exitWalk: () => void;
    isWalking: () => boolean;
    setWalkInput: (forward: number, strafe: number) => void;
    closeApartment: () => void;
    /** Null until the model has loaded. */
    getPlacementContext: () => PlacementContext | null;
    /** The placement brush owns the pointer while painting. */
    setCameraFrozen: (frozen: boolean) => void;
    dispose: () => void;
}

let booted = false;

export function initViewer(callbacks: ViewerCallbacks = {}): ViewerHandle {
    // React 19 StrictMode mounts effects twice in development. initThreeJS
    // builds a renderer, a composer and a tile set; running it twice leaks all
    // three and races the tile loader, so the second call is a no-op.
    if (booted) return handle;
    booted = true;
    disposed = false;

    onProgress = callbacks.onProgress ?? null;
    onLoaded = callbacks.onLoaded ?? null;
    onWalkExit = callbacks.onWalkEnded ?? null;

    initThreeJS();

    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Escape') return;
        if (apartmentPopupActive) closeApartmentPopup();
    };
    document.addEventListener('keydown', onKeyDown);
    keyHandler = onKeyDown;

    return handle;
}

const handle: ViewerHandle = {
    setTimeOfDay: (t) => {
        liveTime = false;
        applyTimeOfDay(t);
    },
    setLiveTime: (on) => {
        liveTime = on;
        liveTimeTimer = 0;
        if (on) applyTimeOfDay(sliderForNow());
    },
    setMarkers: (on) => apartmentBlocks?.setVisible(on),
    setResidenceFilter: (apartmentIndex) => apartmentBlocks?.setFilter(apartmentIndex),
    setBloom: (enabled) => {
        devSettings.bloomEnabled = enabled;
        if (bloomEffect) bloomEffect.blendMode.opacity.value = enabled ? 1 : 0;
    },
    enterWalk: () => walk?.enter() ?? false,
    exitWalk: () => walk?.exit(),
    isWalking: () => walk?.active ?? false,
    setWalkInput: (forward, strafe) => walk?.setMoveInput(forward, strafe),
    closeApartment: () => closeApartmentPopup(),
    getPlacementContext: () => placementContext,
    setCameraFrozen: (frozen) => {
        controls.enabled = !frozen;
    },
    dispose: () => {
        disposed = true;
        placementRuntime?.dispose();
        placementRuntime = null;
        placementContext = null;
        if (rafHandle !== null) cancelAnimationFrame(rafHandle);
        if (keyHandler) document.removeEventListener('keydown', keyHandler);
        window.removeEventListener('resize', onWindowResize);
        window.removeEventListener('resize', handlePopupResize);
        walk?.dispose?.();
        booted = false;
    },
};
